/**
 * 并行 API 批处理：Promise.allSettled + 单条主错误 toast
 * 供首屏并行加载等场景使用
 */

import { toL1Error } from './l1-error'
import { pickPrimaryError, showApiError } from './l1-error-presenter'
import type { AxiosRequestConfig } from 'axios'

/** 批处理 axios 配置（含 skipErrorToast / errorBatchId） */
export type BatchRequestConfig = AxiosRequestConfig & {
  skipErrorToast: true
  errorBatchId: string
}

/** 批处理上下文：供 axios 请求传入 skipErrorToast + errorBatchId */
export interface BatchContext {
  batchId: string
  requestConfig: BatchRequestConfig
}

export interface RunApiBatchOptions {
  batchId?: string
}

/** 接收 batchContext 的 call factory（推荐） */
export type ApiBatchCall<T> = (context: BatchContext) => Promise<T>

/** 无 context 的 call factory（需自行闭包 batchConfig） */
export type ApiBatchCallLegacy<T> = () => Promise<T>

type AnyBatchCall<T> = ApiBatchCall<T> | ApiBatchCallLegacy<T>

function isContextCall<T>(call: AnyBatchCall<T>): call is ApiBatchCall<T> {
  return call.length > 0
}

function generateBatchId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `batch-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/**
 * 创建批处理上下文：生成 batchId 与 axios 请求配置
 */
export function createBatchContext(batchId?: string): BatchContext {
  const id = batchId ?? generateBatchId()
  return {
    batchId: id,
    requestConfig: {
      skipErrorToast: true,
      errorBatchId: id,
    },
  }
}

/** 从 allSettled 结果中提取 rejected 并展示主错误 */
function presentBatchFailures<T>(results: PromiseSettledResult<T>[]): void {
  const rejectedErrors = results
    .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    .map((r) => toL1Error(r.reason))

  if (rejectedErrors.length === 0) {
    return
  }

  const primary = pickPrimaryError(rejectedErrors)
  if (primary) {
    showApiError(primary)
  }
}

/**
 * 并行执行多个 API 调用，失败时汇总展示一条主错误
 * @returns Promise.allSettled 结果数组
 */
export async function runApiBatch<T>(
  calls: Array<AnyBatchCall<T>>,
  options?: RunApiBatchOptions
): Promise<PromiseSettledResult<T>[]> {
  const context = createBatchContext(options?.batchId)

  const results = await Promise.allSettled(
    calls.map((call) => {
      if (isContextCall(call)) {
        return call(context)
      }
      return (call as ApiBatchCallLegacy<T>)()
    }),
  )

  presentBatchFailures(results)
  return results
}

/**
 * Vue composable：提供 run 与 batchConfig，便于在 setup 中并行首屏加载
 */
export function useApiBatch(options?: RunApiBatchOptions) {
  const context = createBatchContext(options?.batchId)

  async function run<T>(
    calls: Array<AnyBatchCall<T>>
  ): Promise<PromiseSettledResult<T>[]> {
    return runApiBatch(calls, { batchId: context.batchId })
  }

  return {
    run,
    batchConfig: context.requestConfig,
    batchId: context.batchId,
  }
}
